import { sidebarCustomersOptions, sidebarEntriesOptions } from "@/api/queryApi";
import { type SidebarEntry } from "@/api/sidebarEntryAPI";
import ErrorPage from "@/ErrorPage";
import useUser from "@/hooks/useUser";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { isAxiosError } from "axios";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { getCustomerScopedPath, isInternalUrl } from "./customerPath";

type HubRedirectProps = {
    customerId?: number;
};

type HubRedirectTarget =
    | {
          type: "route";
          to: "/$customerId/dashboard";
          params: { customerId: string };
      }
    | {
          type: "route";
          to: "/$customerId/dashboard/$profileId";
          params: { customerId: string; profileId: string };
      }
    | {
          type: "route";
          to: "/$customerId/pulse/$pulseId";
          params: { customerId: string; pulseId: string };
      }
    | {
          type: "location";
          url: string;
      };

function getRedirectTarget(
    entry: SidebarEntry | undefined,
    customerId: number | undefined,
): HubRedirectTarget | undefined {
    if (!entry?.url || !customerId) {
        return undefined;
    }

    if (!isInternalUrl(entry.url)) {
        return { type: "location", url: entry.url };
    }

    const customerIdParam = String(customerId);

    if (entry.navigation?.type === "DASHBOARD") {
        const defaultProfile = entry.profiles[0];

        if (defaultProfile) {
            return {
                type: "route",
                to: "/$customerId/dashboard/$profileId",
                params: {
                    customerId: customerIdParam,
                    profileId: String(defaultProfile.id),
                },
            };
        }

        return {
            type: "route",
            to: "/$customerId/dashboard",
            params: { customerId: customerIdParam },
        };
    }

    if (entry.navigation?.type === "PULSE") {
        return {
            type: "route",
            to: "/$customerId/pulse/$pulseId",
            params: {
                customerId: customerIdParam,
                pulseId: String(entry.navigation.pulseInstanceId),
            },
        };
    }

    return { type: "location", url: getCustomerScopedPath(customerId, entry.url) };
}

export default function HubRedirect({ customerId }: HubRedirectProps) {
    const user = useUser();
    const { t } = useTranslation();
    const ownedByCustomerId = user?.ownedByCustomerId ?? undefined;
    const shouldResolveFallbackCustomer =
        customerId === undefined && ownedByCustomerId === undefined;
    const {
        data: sidebarCustomers,
        error: sidebarCustomersError,
        isError: isSidebarCustomersError,
        isPending: isSidebarCustomersPending,
    } = useQuery({
        ...sidebarCustomersOptions(),
        enabled: shouldResolveFallbackCustomer,
    });
    const selectedCustomerId = customerId ?? ownedByCustomerId ?? sidebarCustomers?.[0]?.id;
    const { data, error, isPending, isError } = useQuery(sidebarEntriesOptions(selectedCustomerId));
    const entry = data?.entries.find(
        (sidebarEntry) => sidebarEntry.type !== "LINK" && !sidebarEntry.isUpsell,
    );
    const redirectTarget = useMemo(
        () => getRedirectTarget(entry, selectedCustomerId),
        [entry, selectedCustomerId],
    );
    const navigate = useNavigate();
    const hasNoAvailableCustomers =
        shouldResolveFallbackCustomer &&
        !isSidebarCustomersPending &&
        !isSidebarCustomersError &&
        (sidebarCustomers?.length ?? 0) === 0;
    const shouldNavigateToCustomerPortal =
        customerId === undefined &&
        selectedCustomerId !== undefined &&
        data !== undefined &&
        !entry;

    useEffect(() => {
        if (shouldNavigateToCustomerPortal) {
            void navigate({
                to: "/$customerId",
                params: { customerId: String(selectedCustomerId) },
                replace: true,
            });
            return;
        }

        if (!redirectTarget || redirectTarget.type !== "route") {
            return;
        }

        void navigate({
            to: redirectTarget.to,
            params: redirectTarget.params,
            replace: true,
        });
    }, [navigate, redirectTarget, selectedCustomerId, shouldNavigateToCustomerPortal]);

    if (
        (isSidebarCustomersError &&
            isAxiosError(sidebarCustomersError) &&
            sidebarCustomersError.response?.status === 403) ||
        (isError && isAxiosError(error) && error.response?.status === 403)
    ) {
        return <ErrorPage statusCode="403" />;
    }

    if (hasNoAvailableCustomers) {
        return (
            <ErrorPage
                title={t("hub.noCustomerAccessTitle")}
                description={t("hub.noCustomerAccessDescription")}
            />
        );
    }

    if (
        isSidebarCustomersPending ||
        isPending ||
        isSidebarCustomersError ||
        isError ||
        (!redirectTarget && customerId === undefined)
    ) {
        return null;
    }

    if (!redirectTarget) {
        return <HubEmptyState />;
    }

    if (redirectTarget.type === "route") {
        return null;
    }

    return <LocationRedirect url={redirectTarget.url} />;
}

function HubEmptyState() {
    const { t } = useTranslation();

    return (
        <div className="text-muted-foreground flex flex-1 items-center justify-center p-6 text-center text-sm">
            {t("hub.emptyState")}
        </div>
    );
}

function LocationRedirect({ url }: { url: string }) {
    useEffect(() => {
        window.location.replace(url);
    }, [url]);

    return null;
}
